import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { Squad, User } from '../../../types';
import { getBlockATemplateByCurriculum } from '../../progress/reflections/blockARegistry';

interface Props {
  squad: Squad | null;
  students: User[];
  isOpen: boolean;
  onClose: () => void;
}

const SquadBoardModeModal: React.FC<Props> = ({ squad, students, isOpen, onClose }) => {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completion, setCompletion] = useState<Record<string, boolean>>({});
  const [openQuestionIds, setOpenQuestionIds] = useState<Record<string, boolean>>({});

  const details = useMemo(() => {
    if (!squad) return null;
    const levelId = (squad as any).liveSession?.levelId || squad.currentLessonId || '';
    const sessionId = (squad as any).liveSession?.sessionId || '';
    const curriculumId = (squad as any).curriculumId || 'music';
    const version = ((squad as any).activeReflectionSession?.version || 0) + 1;
    return {
      levelId: String(levelId || ''),
      sessionId: String(sessionId || ''),
      curriculumId: String(curriculumId || 'music'),
      version,
    };
  }, [squad]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isOpen || !squad || !details?.levelId) return;
      const map: Record<string, boolean> = {};
      try {
        await Promise.all(
          (students || []).map(async (stu) => {
            try {
              const ref = doc(db, 'users', stu.id, 'reflectionGame', 'state');
              const snap = await getDoc(ref);
              if (!snap.exists()) {
                map[stu.id] = false;
                return;
              }
              const state = snap.data() as any;
              const bySC = state?.completedLevelsBySquadCurriculum?.[squad.id]?.[details.curriculumId];
              const done = !!(bySC && bySC[details.levelId]);
              const legacy = state?.completedLevels?.[details.levelId];
              map[stu.id] = !!(done || legacy);
            } catch {
              map[stu.id] = false;
            }
          })
        );
      } finally {
        if (!cancelled) setCompletion(map);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, squad?.id, details?.levelId, details?.curriculumId, students]);

  const handleSend = async () => {
    if (!squad || !details) return;
    try {
      setIsSending(true);
      setError(null);
      const squadRef = doc(db, 'squads', squad.id);
      await updateDoc(squadRef, {
        activeReflectionSession: {
          levelId: details.levelId,
          curriculumId: details.curriculumId,
          sessionId: details.sessionId,
          version: details.version,
          createdAt: serverTimestamp(),
        },
      });
    } catch (e) {
      console.error('Failed to send reflections from Board Mode', e);
      setError('Failed to send reflections. Try again.');
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen || !squad || !details) return null;

  const template = getBlockATemplateByCurriculum(details.levelId, details.curriculumId);

  const disabledReason = !details.levelId
    ? 'No live level set'
    : !details.sessionId
    ? 'No active session'
    : undefined;

  return (
    <div className="attendance-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="attendance-modal" style={{ width: '95%', maxWidth: 960, display: 'flex', flexDirection: 'column' }}>
        <div className="attendance-modal-header">
          <h2>{details.curriculumId.toLowerCase().startsWith('music') ? 'Reflection' : 'Reflect and Progress'}</h2>
          <button className="close-modal-btn" onClick={onClose}>×</button>
        </div>

        <div className="attendance-modal-content" style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div><strong>Level</strong>: {details.levelId}</div>
            <div><strong>Curriculum</strong>: {details.curriculumId}</div>
          </div>

          <div style={{ padding: '8px 0' }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Questions</div>
            {!template ? (
              <div>No Block A template found for this level.</div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {template.questions.map((q) => {
                  const isOpen = !!openQuestionIds[q.id];
                  const toggle = () => setOpenQuestionIds((m) => ({ ...m, [q.id]: !m[q.id] }));
                  return (
                    <div key={q.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                      <button
                        onClick={toggle}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '10px 12px',
                          background: 'white',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                        aria-expanded={isOpen}
                        aria-controls={`q-panel-${q.id}`}
                      >
                        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', flex: 1 }}>{q.prompt}</span>
                        <span style={{ transform: `rotate(${isOpen ? 90 : 0}deg)`, transition: 'transform 0.15s ease' }}>➤</span>
                      </button>
                      {isOpen && (
                        <div id={`q-panel-${q.id}`} style={{ padding: '8px 12px', background: '#fafafa', borderTop: '1px solid #e5e7eb' }}>
                          {q.type === 'multiple_choice' && Array.isArray(q.options) ? (
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {q.options.map((opt: string, idx: number) => (
                                <li key={idx} style={{ marginBottom: 4 }}>{opt}</li>
                              ))}
                            </ul>
                          ) : q.type === 'emoji_rating' && q.emojiRange ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 600 }}>Scale {q.emojiRange.min}–{q.emojiRange.max}:</span>
                              <span>{(q.emojiRange.emojis || []).join(' ')}</span>
                            </div>
                          ) : (
                            <div>All possible answers will appear here.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Students</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
              {students.map((stu) => {
                const avatar = stu.selectedMuse?.img || null;
                const name = stu.displayName || 'Student';
                return (
                  <div key={stu.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {avatar ? (
                      <img src={avatar} alt={name} style={{ width: 28, height: 28, borderRadius: '50%' }} />
                    ) : (
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e5e7eb' }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{name}</div>
                      <div style={{ fontSize: 12, color: completion[stu.id] ? '#059669' : '#6b7280' }}>
                        {completion[stu.id] ? 'Answered' : 'Not yet'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
        </div>

        <div className="attendance-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleSend}
              disabled={!!disabledReason || isSending}
              style={{
                padding: '0.6rem 1rem',
                borderRadius: 999,
                border: 'none',
                background: disabledReason ? '#e5e7eb' : '#3b82f6',
                color: disabledReason ? '#6b7280' : 'white',
                cursor: disabledReason ? 'not-allowed' : 'pointer',
                fontWeight: 700,
              }}
              title={disabledReason || ''}
            >
              {isSending ? 'Sending…' : 'Send / Resend'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SquadBoardModeModal;
